/**
 * ログインページ - Neo-Futuristic HUD
 */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// HUD装飾: フォームカードの四隅ブラケット
function InstrumentBrackets() {
  return (
    <>
      <div className="absolute top-0 left-0 border-l-4 border-t-4 border-primary w-8 h-8 pointer-events-none" />
      <div className="absolute top-0 right-0 border-r-4 border-t-4 border-primary w-8 h-8 pointer-events-none" />
      <div className="absolute bottom-0 left-0 border-l-4 border-b-4 border-primary w-8 h-8 pointer-events-none" />
      <div className="absolute bottom-0 right-0 border-r-4 border-b-4 border-primary w-8 h-8 pointer-events-none" />
    </>
  );
}

// 入力フィールド: reticleコーナー + アンダーライン
function HudInput({ label, icon, ...props }) {
  return (
    <div className="reticle-group group relative flex flex-col gap-2">
      <div className="reticle-corner top-0 left-0 border-l-2 border-t-2" style={{ top: 0, left: 0 }} />
      <div className="reticle-corner top-0 right-0 border-r-2 border-t-2" style={{ top: 0, right: 0 }} />
      <div className="reticle-corner bottom-0 left-0 border-l-2 border-b-2" style={{ bottom: 0, left: 0 }} />
      <div className="reticle-corner bottom-0 right-0 border-r-2 border-b-2" style={{ bottom: 0, right: 0 }} />
      <div className="px-4 pt-4 pb-1">
        <label className="font-label-caps text-label-caps text-primary/60 block mb-1">{label}</label>
        <div className="relative">
          {icon && (
            <span className="material-symbols-outlined absolute left-0 top-1/2 -translate-y-1/2 text-primary/40 group-focus-within:text-primary transition-colors text-xl select-none">
              {icon}
            </span>
          )}
          <input
            className="w-full bg-transparent border-none focus:ring-0 py-3 pl-8 pr-2 text-on-surface font-body-sm placeholder:text-outline outline-none"
            {...props}
          />
        </div>
      </div>
    </div>
  );
}

// 送信ボタン: 四角 + box-shadowグロー
function HudButton({ children, loading, icon, ...props }) {
  return (
    <button
      className="rounded-none w-full h-16 bg-primary text-on-primary font-label-caps text-label-caps tracking-[0.2em] hover:shadow-[0_0_30px_rgba(162,201,255,0.9)] active:scale-[0.98] transition-all flex items-center justify-center gap-3 group relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
      {...props}
    >
      <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
      {loading ? (
        <span className="flex items-center gap-2">
          <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          PROCESSING...
        </span>
      ) : (
        <>
          <span>{children}</span>
          {icon && (
            <span className="material-symbols-outlined text-[20px] group-hover:translate-x-1 transition-transform" style={{ fontSize: '20px' }}>
              {icon}
            </span>
          )}
        </>
      )}
    </button>
  );
}

// エラー表示
function HudAlert({ message }) {
  if (!message) return null;
  return (
    <div className="border border-error/50 bg-error-container/30 px-4 py-3 flex items-center gap-3">
      <span className="material-symbols-outlined text-error text-base select-none">warning</span>
      <span className="font-label-caps text-[11px] text-error tracking-wider">{message}</span>
    </div>
  );
}

// 初回パスワード変更画面
const FOLD_KEYFRAMES = [
  { opacity: 1, transform: 'perspective(900px) rotateX(0deg) translateY(0) scale(1)' },
  { opacity: 0, transform: 'perspective(900px) rotateX(30deg) translateY(48px) scale(0.96)' },
];
const FOLD_OPTIONS = { duration: 400, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };

function NewPasswordForm({ onSubmit, isLoading, error, sectionRef }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');
    if (newPassword !== confirmPassword) {
      setLocalError('パスワードが一致しません');
      return;
    }
    if (newPassword.length < 8) {
      setLocalError('パスワードは8文字以上で入力してください');
      return;
    }
    onSubmit(newPassword);
  };

  return (
    <section ref={sectionRef} className="hud-rise glass-panel relative p-8 lg:p-12 overflow-hidden">
      <InstrumentBrackets />
      <div className="text-center mb-10">
        <h1 className="font-headline-xl text-headline-xl text-primary tracking-[0.2em] italic font-black uppercase mb-1">
          AIRGROW
        </h1>
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="h-[1px] w-8 bg-primary/30" />
          <p className="font-label-caps text-[10px] text-primary/70 tracking-[0.4em]">FLIGNT PLANNER V0.01</p>
          <div className="h-[1px] w-8 bg-primary/30" />
        </div>
        <div className="bg-primary/10 py-1 px-4 inline-block">
          <span className="font-label-caps text-[11px] text-primary tracking-widest uppercase">
            New Credentials Required
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-8">
        <HudAlert message={localError || error} />
        <div className="flex flex-col gap-6">
          <HudInput
            label="NEW_ENCRYPTION_KEY"
            icon="lock"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="8文字以上"
            required
          />
          <HudInput
            label="CONFIRM_KEY"
            icon="lock_reset"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="パスワード再入力"
            required
          />
        </div>
        <HudButton type="submit" loading={isLoading} icon="check_circle">
          AUTHORIZE_UPLINK
        </HudButton>
      </form>
    </section>
  );
}

// メインログイン画面
export default function Login() {
  const { login, commitLogin, setNewPassword, commitNewPassword, pendingUser, error } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const sectionRef = useRef(null);

  // アニメーション完了後にコールバックを実行する
  const animateOut = useCallback((onComplete) => {
    const el = sectionRef.current;
    if (el) {
      const anim = el.animate(FOLD_KEYFRAMES, FOLD_OPTIONS);
      anim.onfinish = onComplete;
    } else {
      onComplete();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    setIsLoading(true);
    let succeeded = false;
    try {
      const result = await login(email, password);
      if (result.success) {
        succeeded = true;
        // アニメーション完了後に auth 状態をコミット → PublicRoute が自動で "/" にリダイレクト
        animateOut(() => commitLogin(result.email));
      }
    } catch (err) {
      setLocalError(err.message || 'ログインに失敗しました');
    } finally {
      if (!succeeded) setIsLoading(false);
    }
  };

  const handleNewPassword = async (newPass) => {
    setIsLoading(true);
    let succeeded = false;
    try {
      const result = await setNewPassword(newPass);
      if (result.success) {
        succeeded = true;
        animateOut(() => commitNewPassword());
      }
    } catch (err) {
      setLocalError(err.message || 'パスワード変更に失敗しました');
    } finally {
      if (!succeeded) setIsLoading(false);
    }
  };

  return (
    <div className="bg-background text-on-background font-body-lg min-h-screen overflow-hidden">
      {/* 背景レイヤー */}
      <div className="fixed inset-0 z-0">
        <img
          alt="Tactical Drone Fleet"
          className="w-full h-full object-cover"
          src="https://lh3.googleusercontent.com/aida-public/AB6AXuCBRp9Rp9b_9FZD6pgs42HKarUwW9BzoZe45sKxLFhyGVQSPYHrlWopngzNaO87ZU5jasOVmT_OJGRE07M6oT787R_6tg6_9nezcCGRamtkONcwoQmRw6wBzVWJ7P7huualqYfzSjNY409rmqJucpzG3BHhOADzhhjrjNmfhJ8RpIqoE1G4oDrSLFFwY0na1L_Sndiy4po4YzbzVyLZoSC3lKFRn9sEMZ9Z4oA8hCCccysp1xFiiS9qDGu_4YRos4X9CXyWmn10Yg"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-transparent to-slate-950/80" />
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute inset-0 scanline pointer-events-none" />
        <div className="absolute inset-0 opacity-10 animate-scan bg-gradient-to-b from-transparent via-primary/20 to-transparent h-1/2" />
      </div>

      {/* HUDレイアウト */}
      <main className="relative z-10 flex flex-col items-center justify-center min-h-screen p-gutter">
        {/* 上部HUD装飾 */}
        <div className="hud-fade-in absolute top-margin left-margin right-margin flex justify-between items-start pointer-events-none" style={{ top: '32px', left: '32px', right: '32px', animationDelay: '0.6s' }}>
          <div className="hidden lg:flex flex-col gap-2">
            <span className="font-label-caps text-label-caps text-primary opacity-60 blur-[1px]">MISSION_STATUS</span>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              <span className="font-headline-md text-secondary blur-[1.5px] opacity-70">▰▱■▰_▱■▰▱</span>
            </div>
          </div>
          <div className="hidden lg:flex flex-col items-end gap-2 text-right">
            <span className="font-label-caps text-label-caps text-primary opacity-60 blur-[1px]">TELEMETRY_LINK</span>
            <span className="font-body-sm text-primary/80">▰▱ ⧖ ▫ ■ ■ ▫ ⧖</span>
          </div>
        </div>

        {/* 中央フォームエリア */}
        <div className="relative w-full max-w-lg">
          {/* 回転レティクル装飾 */}
          <div className="hud-fade-in absolute -inset-12 border-2 border-primary/10 rounded-full pointer-events-none border-dashed animate-[spin_60s_linear_infinite]" style={{ animationDelay: '0.8s' }} />
          <div className="hud-fade-in absolute -inset-16 border border-primary/5 rounded-full pointer-events-none" style={{ animationDelay: '0.9s' }} />

          {/* 左サイドゲージ */}
          <div className="hud-fade-in absolute -left-32 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-end gap-12 pointer-events-none" style={{ animationDelay: '0.7s' }}>
            <div className="flex flex-col items-end">
              <span className="font-label-caps text-[10px] text-primary/40 blur-[2px]">ALTITUDE</span>
              <span className="font-headline-md text-primary/80">■■▰ ▫▫</span>
              <div className="w-24 h-1 bg-primary/20 mt-1"><div className="w-2/3 h-full bg-primary/60" /></div>
            </div>
            <div className="flex flex-col items-end">
              <span className="font-label-caps text-[10px] text-primary/40 blur-[2px]">VELOCITY</span>
              <span className="font-headline-md text-primary/80">▱ ⧖.▰▰</span>
              <div className="w-24 h-1 bg-primary/20 mt-1"><div className="w-4/5 h-full bg-primary/60" /></div>
            </div>
          </div>

          {/* フォームカード */}
          {pendingUser ? (
            <NewPasswordForm
              onSubmit={handleNewPassword}
              isLoading={isLoading}
              error={localError || error}
              sectionRef={sectionRef}
            />
          ) : (
            <section ref={sectionRef} className="hud-rise glass-panel relative p-8 lg:p-12 overflow-hidden">
              <InstrumentBrackets />

              {/* ヘッダー */}
              <div className="text-center mb-10">
                <h1 className="font-headline-xl text-headline-xl text-primary tracking-[0.2em] italic font-black uppercase mb-1">
                  AIRGROW
                </h1>
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="h-[1px] w-8 bg-primary/30" />
                  <p className="font-label-caps text-[10px] text-primary/70 tracking-[0.4em]">FLIGNT PLANNER V0.01</p>
                  <div className="h-[1px] w-8 bg-primary/30" />
                </div>
                <div className="bg-primary/10 py-1 px-4 inline-block">
                  <span className="font-label-caps text-[11px] text-primary tracking-widest uppercase">
                    Operator Authentication Required
                  </span>
                </div>
              </div>

              {/* 認証フォーム */}
              <form onSubmit={handleLogin} className="flex flex-col gap-8">
                <div className="flex flex-col gap-6">
                  <HudAlert message={localError || error} />

                  <HudInput
                    label="MAIL ADDRESS"
                    icon="person"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="operator@example.com"
                    required
                    autoFocus
                  />

                  {/* パスワードフィールド（表示切替ボタン付き） */}
                  <div className="reticle-group group relative flex flex-col gap-2">
                    <div className="reticle-corner border-l-2 border-t-2" style={{ top: 0, left: 0 }} />
                    <div className="reticle-corner border-r-2 border-t-2" style={{ top: 0, right: 0 }} />
                    <div className="reticle-corner border-l-2 border-b-2" style={{ bottom: 0, left: 0 }} />
                    <div className="reticle-corner border-r-2 border-b-2" style={{ bottom: 0, right: 0 }} />
                    <div className="px-4 pt-4 pb-1">
                      <label className="font-label-caps text-label-caps text-primary/60 block mb-1">
                        PASSWORD
                      </label>
                      <div className="relative">
                        <span className="material-symbols-outlined absolute left-0 top-1/2 -translate-y-1/2 text-primary/40 transition-colors text-xl select-none">
                          lock
                        </span>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          required
                          className="w-full bg-transparent border-none focus:ring-0 py-3 pl-8 pr-10 text-on-surface font-body-sm placeholder:text-outline outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-0 top-1/2 -translate-y-1/2 text-primary/40 hover:text-primary transition-colors"
                        >
                          <span className="material-symbols-outlined text-xl">
                            {showPassword ? 'visibility_off' : 'visibility'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* アクション */}
                <div className="flex flex-col gap-6">
                  <HudButton type="submit" loading={isLoading} icon="rocket_launch">
                    LOGIN
                  </HudButton>
                  <div className="flex justify-center items-center gap-2 px-2">
                    <span className="font-label-caps text-[10px] text-primary/40">
                      アカウントをお持ちでない方は
                    </span>
                    <button
                      type="button"
                      onClick={() => animateOut(() => navigate('/register'))}
                      className="font-label-caps text-[10px] text-primary hover:text-on-primary-container transition-colors border-b border-primary/40 hover:border-primary"
                    >
                      新規登録
                    </button>
                  </div>
                </div>
              </form>
            </section>
          )}
        </div>

        {/* 下部HUD装飾 */}
        <div className="hud-fade-in absolute pointer-events-none" style={{ bottom: '32px', left: '32px', right: '32px', animationDelay: '0.6s' }}>
          <div className="flex justify-between items-end">
            <div className="hidden lg:flex flex-col gap-1">
              <span className="font-label-caps text-[10px] text-primary/30 uppercase blur-[1px]">System Integrity</span>
              <div className="flex gap-1">
                <div className="w-1 h-3 bg-secondary" />
                <div className="w-1 h-3 bg-secondary" />
                <div className="w-1 h-3 bg-secondary" />
                <div className="w-1 h-3 bg-secondary" />
                <div className="w-1 h-3 bg-secondary/30" />
              </div>
            </div>
            <div className="text-right">
              <span className="font-label-caps text-[10px] text-primary/40 block">θ.9 ■ X7-β ▰▱</span>
              <span className="font-label-caps text-[10px] text-primary/40 block">⧖ ▫ ■■-09-Ω</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
